/* My Work — the cross-client work inbox (#/work, rail item 02), Package 5.
 *
 * A frontend port of the owner's approved export (crm-hub/my-work.html, S4):
 * a greeting head, a workload summary strip, a filter toolbar, and STATE-ordered
 * lanes (overdue → waiting-on-external → ready-to-verify → open) down the middle
 * with a context sidecard. It is a VIEW of the ONE task ledger — the
 * session-scoped GET /me/tasks projection (lib/myWork.ts) — never a second
 * store, and it orders by STATE, never by a priority the system guessed.
 *
 * Data path (build brief): /me/tasks EXISTS (service/app/routers/workspace.py
 * — the cross-client "needs you" projection for the signed-in user, already
 * assignee ∪ watcher ∪ mention), so this screen uses it directly rather than
 * aggregating per-client boards. Client identity (logo + the deep-link run
 * file) is joined from the shared GET /clients roster (views/register.ts's
 * onClientsLoaded — no second fetch).
 *
 * Honesty: absent data renders honestly — an empty lane says so, an empty inbox
 * says so, and "Today's meetings" reads "calendar not connected" (no calendar
 * source exists). No mockup fixture numbers are copied; every count is derived
 * from the live rows.
 *
 * Lit conventions follow views/register.ts / views/wiki.ts exactly.
 */
import { html, render, type TemplateResult } from 'lit';
import { ApiError } from '../api/client.ts';
import { AUTH } from '../lib/auth.ts';
import { currentRoute } from '../lib/router.ts';
import { brandVisual } from '../lib/identity.ts';
import { buildTaskHref } from '../ws/taskLink.ts';
import { brandLogo, onClientsLoaded, type RegisterClient } from './register.ts';
import {
  LANE_META, LANE_ORDER, fetchMyWork, groupByLane, humanStatus, laneCounts,
  openAssignedCount, workRowState,
  type MyWorkReason, type MyWorkRow, type StateCls, type WorkLane,
} from '../lib/myWork.ts';

type RelationFilter = 'all' | 'assigned' | 'watched' | 'mentioned';

let rows: MyWorkRow[] = [];
let loaded = false;
let errored = false;
let relation: RelationFilter = 'all';
let clientFilter = 'all';
/** slug → its roster row, for brand logos + the deep-link run file. Populated
 * from the ONE shared GET /clients (no second fetch). */
const roster = new Map<string, RegisterClient>();

onClientsLoaded((clients) => {
  roster.clear();
  for (const c of clients) roster.set(c.slug, c);
  // Upgrade neutral marks → real logos once the roster lands — but only repaint
  // when My Work is the on-screen area (painting into the hidden #workView on
  // another page would leak this view's summary cells into that page's DOM).
  if (loaded && onScreen()) paint();
});

/** True when My Work (#/work) is the visible area. Boot fetches /me/tasks on
 * every page for the live rail badge, but the FULL view is painted lazily —
 * only when its section is actually on screen (so it never adds stray DOM,
 * e.g. summary cells, to another route's page). */
function onScreen(): boolean {
  return currentRoute().area === 'mywork';
}

/* ── rail badge (live count of the user's OPEN assigned items) ─────────────── */
function setWorkBadge(count: number): void {
  const badge = document.getElementById('workBadge');
  if (!badge) return;
  badge.textContent = String(count);
  badge.toggleAttribute('hidden', count === 0);
}

/* ── head ──────────────────────────────────────────────────────────────────── */
function firstName(name: string | null | undefined): string {
  const w = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  return w[0] || 'there';
}

function headView(): TemplateResult {
  return html`<div class="portfolio-head">
    <div>
      <span class="eyebrow">02 · account management · across your book</span>
      <h1>My Work</h1>
      <p>Everything assigned to or watched by ${firstName(AUTH.user?.name)} across indexed clients — one ledger,
        ordered by <strong>state</strong> (overdue · waiting · ready to verify), not by a priority the system guessed.</p>
    </div>
  </div>`;
}

/* ── summary strip (derived cells only) ────────────────────────────────────── */
function summaryView(): TemplateResult {
  const c = laneCounts(rows);
  const openMine = openAssignedCount(rows);
  const cell = (label: string, value: number | string, sub: string, cls = ''): TemplateResult => html`
    <div class="summary-cell ${cls}"><span>${label}</span><strong>${value}</strong><small>${sub}</small></div>`;
  const clientsWith = new Set(rows.map((r) => r.client_slug)).size;
  return html`<div class="portfolio-summary" aria-label="Your workload">
    ${cell('Open · assigned to you', openMine, clientsWith ? `across ${clientsWith} ledger${clientsWith === 1 ? '' : 's'}` : 'nothing assigned')}
    ${cell('Overdue', c.overdue, c.overdue ? 'past a connected due date' : 'none past due')}
    ${cell('Waiting on external', c.waiting, c.waiting ? 'editor / platform / client gates' : 'nothing waiting')}
    ${cell('Ready to verify', c.ready, c.ready ? 'evidence probe · ~$0' : 'none to verify')}
    ${cell('Today’s meetings', '—', 'calendar not connected', 'unavailable')}
  </div>`;
}

/* ── toolbar (client-side filters: relation + per-client) ──────────────────── */
function setRelation(r: RelationFilter): void { relation = r; paint(); }
function setClient(slug: string): void { clientFilter = slug; paint(); }

function toolbarView(): TemplateResult {
  const chip = (id: RelationFilter, label: string): TemplateResult => html`
    <button class=${relation === id ? 'active' : ''} @click=${() => setRelation(id)}>${label}</button>`;
  const clientSlugs = [...new Set(rows.map((r) => r.client_slug))]
    .map((slug) => ({ slug, brand: rows.find((r) => r.client_slug === slug)?.brand || slug }))
    .sort((a, b) => a.brand.localeCompare(b.brand));
  return html`<div class="client-register-tools">
    <div class="filter-group" aria-label="Relation filters">
      ${chip('all', 'All')}
      ${chip('assigned', 'Assigned to me')}
      ${chip('watched', 'Watching')}
      ${chip('mentioned', 'Mentions')}
    </div>
    <label class="columns-button" style="display:inline-flex;align-items:center;gap:var(--sp-1)">
      Client
      <select aria-label="Filter by client" .value=${clientFilter}
        @change=${(ev: Event) => setClient((ev.target as HTMLSelectElement).value)}
        style="border:0;background:transparent;color:inherit;font:inherit;cursor:pointer">
        <option value="all">All clients</option>
        ${clientSlugs.map((c) => html`<option value=${c.slug} ?selected=${clientFilter === c.slug}>${c.brand}</option>`)}
      </select>
    </label>
  </div>`;
}

function matchesFilters(r: MyWorkRow): boolean {
  if (relation !== 'all' && r.reason !== (relation as MyWorkReason)) return false;
  if (clientFilter !== 'all' && r.client_slug !== clientFilter) return false;
  return true;
}

/* ── one work row ──────────────────────────────────────────────────────────── */
const STATE_CLS: Record<StateCls, string> = {
  attention: 'attention', waiting: 'waiting', calm: 'calm',
  priority: 'priority', mention: 'mention', plain: 'plain',
};

const REASON_LABEL: Record<MyWorkReason, string> = {
  assigned: 'assigned to you', watched: 'watching', mentioned: 'you were @mentioned',
};

/** Days a row has sat in its current state (from status_changed_at — the stall
 * clock, never updated_at). null when unknown. */
function daysInState(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / 864e5));
}

function shortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function rowMeta(r: MyWorkRow): string {
  const bits: string[] = [REASON_LABEL[r.reason]];
  const due = shortDate(r.due_date);
  if (due) bits.push(`due ${due}`);
  const age = daysInState(r.status_changed_at);
  if (age !== null) bits.push(`${age}d in ${humanStatus(r.status).toLowerCase()}`);
  return bits.join(' · ');
}

function rowView(r: MyWorkRow): TemplateResult {
  const client = roster.get(r.client_slug);
  const logoV = brandVisual({ domain: client?.domain, logoUrl: client?.logoUrl });
  const state = workRowState(r);
  const href = buildTaskHref({ clientSlug: r.client_slug, taskId: r.task_id, runFile: client?.lastRunFile });
  const age = daysInState(r.status_changed_at);
  return html`<a class="work-row" href=${href}>
    <span class="work-state"><i class="status-label ${STATE_CLS[state.cls]}">${state.label}</i></span>
    <span class="work-client">${brandLogo(logoV)}<span>${r.brand}</span></span>
    <span class="work-title"><strong>${r.title}</strong><small>${rowMeta(r)}</small></span>
    <span class="work-evidence">
      <b>${humanStatus(r.status)}</b>
      <span>${age !== null ? `${age}d in state` : `priority ${r.priority}`}</span>
    </span>
    <span class="work-go">Open →</span>
  </a>`;
}

/* ── lanes ─────────────────────────────────────────────────────────────────── */
function laneView(lane: WorkLane, laneRows: MyWorkRow[]): TemplateResult {
  const m = LANE_META[lane];
  return html`<section class="worklane" aria-label=${m.title}>
    <div class="worklane-head ${m.headClass}">
      <span class="worklane-dot"></span><h2>${m.title}</h2><b>${laneRows.length}</b><small>${m.blurb}</small>
    </div>
    ${laneRows.length
      ? laneRows.map(rowView)
      : html`<div class="worklane-more" style="cursor:default;color:var(--crm-tertiary)">Nothing ${m.title.toLowerCase()} right now.</div>`}
  </section>`;
}

/* ── context sidecard (honest) ─────────────────────────────────────────────── */
function sideView(): TemplateResult {
  const byClient = new Map<string, { brand: string; open: number; overdue: number }>();
  for (const r of rows) {
    const e = byClient.get(r.client_slug) || { brand: r.brand, open: 0, overdue: 0 };
    e.open += 1;
    if (r.flags.overdue) e.overdue += 1;
    byClient.set(r.client_slug, e);
  }
  const clients = [...byClient.values()].sort((a, b) => a.brand.localeCompare(b.brand));
  return html`<aside class="worklane-side" aria-label="Context">
    <section>
      <div class="rail-panel-head"><span class="eyebrow">Your book</span>
        <span class="wire-tag verified-tag">${clients.length} in view</span></div>
      ${clients.length
        ? html`<dl>${clients.map((c) => html`<div><dt>${c.brand}</dt>
            <dd>${c.open} item${c.open === 1 ? '' : 's'}${c.overdue ? ` · ${c.overdue} overdue` : ''}</dd></div>`)}</dl>`
        : html`<p style="color:var(--crm-tertiary);font-size:.62rem;margin-top:var(--sp-2)">No items across your indexed clients.</p>`}
      <a class="mini-link" href="#/clients">Open the client book →</a>
    </section>
    <section>
      <div class="rail-panel-head"><span class="eyebrow">Today</span><span class="wire-tag">Not connected</span></div>
      <div class="empty-integration"><strong>No calendar or meeting source</strong>
        <p>When a calendar and transcript source is connected, today’s client meetings and their sourced
          action items surface here — not before.</p></div>
    </section>
  </aside>`;
}

/* ── page ──────────────────────────────────────────────────────────────────── */
function view(): TemplateResult {
  if (!loaded && !errored) {
    return html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">Loading your work…</div>`;
  }
  if (errored) {
    return html`<div class="register-empty" style="padding:var(--sp-5);color:var(--crm-tertiary);font-size:.78rem">Workspace service unreachable —
      start the service and reload.</div>`;
  }
  const visible = rows.filter(matchesFilters);
  const g = groupByLane(visible);
  const totalOpen = openAssignedCount(rows);
  return html`
    ${headView()}
    ${summaryView()}
    ${toolbarView()}
    <div class="worklane-wrap">
      <div class="worklane-main">
        ${rows.length === 0
          ? html`<section class="worklane"><div class="worklane-head"><span class="worklane-dot"></span>
              <h2>All clear</h2><small>Nothing is assigned to, watched by, or @mentioning you across the indexed clients.</small></div></section>`
          : visible.length === 0
            ? html`<section class="worklane"><div class="worklane-head"><span class="worklane-dot"></span>
                <h2>No matches</h2><small>No work matches this filter — clear it to see all ${totalOpen} open item${totalOpen === 1 ? '' : 's'}.</small></div></section>`
            : LANE_ORDER.map((lane) => laneView(lane, g[lane]))}
      </div>
      ${sideView()}
    </div>`;
}

function paint(): void {
  const host = document.getElementById('workView');
  if (host) render(view(), host);
}

/** Fetch /me/tasks, paint the inbox, and refresh the rail badge. Called at boot
 * (badge stays live even when the arrival isn't #/work — it paints into the
 * hidden #workView) and on navigation into #/work. A 401 is handled by the api
 * client (it re-shows the sign-in gate). */
export async function initWork(): Promise<void> {
  const host = document.getElementById('workView');
  if (!host) return;
  if (!loaded && onScreen()) paint(); // loading state on first on-screen entry
  try {
    const r = await fetchMyWork();
    rows = r.tasks;
    loaded = true;
    errored = false;
    setWorkBadge(openAssignedCount(rows)); // the rail badge is live on every page
    if (onScreen()) paint();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return; // gate is showing
    errored = true;
    loaded = true;
    if (onScreen()) paint();
  }
}
